import React, { useState, ChangeEvent, useEffect } from "react";
import { FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { InfoIcon } from "lucide-react";
import { applyMask, removeMask } from '@/utils/maskUtils';

interface SimplifiedStringFieldProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  description?: string;
  required?: boolean;
  videoUrl?: string;
}

export const SimplifiedStringField: React.FC<SimplifiedStringFieldProps> = ({
  value,
  onChange,
  label,
  description,
  required = false,
  videoUrl,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [fieldType, setFieldType] = useState<{
    isPassword: boolean;
    mask: string | undefined;
  }>({ isPassword: false, mask: undefined });

  // Função para determinar o tipo do campo baseado no label/nome
  useEffect(() => {
    const normalizedLabel = label.toLowerCase().trim();
    
    // Configuração do campo baseado no nome/label
    const fieldConfig = {
      isPassword: /senha|password|chave|key|token|secret/i.test(normalizedLabel),
      mask: (() => {
        if (/cpf/i.test(normalizedLabel)) return "###.###.###-##";
        if (/cnpj/i.test(normalizedLabel)) return "##.###.###/####-##";
        if (/telefone|celular|fone|phone/i.test(normalizedLabel)) return "(##) #####-####";
        if (/cep|postal/i.test(normalizedLabel)) return "#####-###";
        if (/data|date/i.test(normalizedLabel)) return "##/##/####";
        return undefined;
      })()
    };

    setFieldType(fieldConfig);
  }, [label]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    
    // Se há uma máscara definida, aplica a máscara
    if (fieldType.mask) {
      newValue = applyMask(newValue, fieldType.mask);
    }
    
    onChange(newValue);
  };

  return (
    <div className="w-full">
      <div className="flex items-center mb-2">
        <label className="text-slate-500 font-bold px-2">
          {label}{required && <span className="text-red-500">*</span>}
        </label>
        {description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger><InfoIcon size={16} className="text-grey-light ml-1" /></TooltipTrigger>
              <TooltipContent>
                <p>{description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className="relative">
        <Input 
          type={fieldType.isPassword && !showPassword ? "password" : "text"}
          value={value} 
          onChange={handleChange}
          className={`w-full px-5 py-2.5 text-base placeholder:text-grey-light ${fieldType.isPassword ? "pr-10" : ""}`}
          placeholder={fieldType.mask ? `Formato: ${fieldType.mask}` : `Digite ${label.toLowerCase()}...`}
          required={required}
        />
        {fieldType.isPassword && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-white/70 hover:text-white"
            >
              {showPassword ? (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.79 10.79L9.83 9.83C9.24 10.42 8.42 10.75 7.5 10.75C5.84 10.75 4.5 9.41 4.5 7.75C4.5 6.83 4.83 6.01 5.42 5.42L4.46 4.46C3.69 5.23 3.25 6.25 3.25 7.75C3.25 10.1 5.15 12 7.5 12C8.75 12 9.77 11.56 10.54 10.79H10.79Z"
                    fill="currentColor"
                  />
                  <path
                    d="M7.5 4.25C9.16 4.25 10.5 5.59 10.5 7.25C10.5 8.17 10.17 8.99 9.58 9.58L10.54 10.54C11.31 9.77 11.75 8.75 11.75 7.25C11.75 4.9 9.85 3 7.5 3C6.25 3 5.23 3.44 4.46 4.21L5.42 5.17C6.01 4.58 6.83 4.25 7.5 4.25Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1.5 1.5L13.5 13.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 3C5.65 3 3.75 4.9 3.75 7.25C3.75 9.6 5.65 11.5 8 11.5C10.35 11.5 12.25 9.6 12.25 7.25C12.25 4.9 10.35 3 8 3ZM8 9.75C6.34 9.75 5 8.41 5 6.75C5 5.09 6.34 3.75 8 3.75C9.66 3.75 11 5.09 11 6.75C11 8.41 9.66 9.75 8 9.75Z"
                    fill="currentColor"
                  />
                  <path
                    d="M8 5.5C7.17 5.5 6.5 6.17 6.5 7C6.5 7.83 7.17 8.5 8 8.5C8.83 8.5 9.5 7.83 9.5 7C9.5 6.17 8.83 5.5 8 5.5Z"
                    fill="currentColor"
                  />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}; 